import * as XLSX from "xlsx";

/**
 * Maps and exports lead/deal data to an Excel file.
 * @param {Array} deals - Array of deal objects from the pipeline.
 */
/**
 * Maps and exports lead/deal data to an Excel file.
 * @param {Array} deals - Array of deal objects from the pipeline.
 * @param {Array} selectedFields - Array of field keys to export.
 */
export const exportDealsToExcel = (deals, selectedFields) => {
  if (!deals || deals.length === 0) return;

  // Define all possible fields and their labels/mapping logic
  const FIELD_MAPPING = {
    dealName: { label: "Lead Name", map: (d) => d.dealName || "—" },
    contactName: { label: "Customer Name", map: (d) => d.contactName || "—" },
    mobile: { label: "Mobile", map: (d) => d.mobile || "—" },
    email: { label: "Email", map: (d) => d.email || "—" },
    amount: { label: "Amount", map: (d) => d.amount || 0 },
    stage: { label: "Stage", map: (d) => d.stageName || "—" },
    company: { label: "Company", map: (d) => d.company || "—" },
    priority: { label: "Priority", map: (d) => d.priority || "—" },
    source: { label: "Source", map: (d) => d.source || "—" },
    city: { label: "Location", map: (d) => d.city || "—" },
    interest: { label: "Category", map: (d) => d.interest || "—" },
    subInterest: { label: "Sub Category", map: (d) => d.subInterest || "—" },
    disposition: { label: "Disposition", map: (d) => d.disposition || "—" },
    subDisposition: { label: "Sub Disposition", map: (d) => d.subDisposition || "—" },
    createdAt: { label: "Created Date", map: (d) => d.createdAt ? new Date(d.createdAt).toLocaleDateString("en-GB") : "—" },
    closingDate: { label: "Closing Date", map: (d) => d.closingDate ? new Date(d.closingDate).toLocaleDateString("en-GB") : "—" },
    followUpDate: { label: "Follow Up Date", map: (d) => d.followUpDate ? new Date(d.followUpDate).toLocaleDateString("en-GB") : "—" },
    description: { label: "Description", map: (d) => d.description || "—" },
    remarks: { label: "Remarks", map: (d) => d.remarks || "—" },
    notes: {
      label: "Notes",
      map: (d) => {
        if (!d.notes || (Array.isArray(d.notes) && d.notes.length === 0)) {
          return "—";
        }
        if (Array.isArray(d.notes)) {
          return (
            d.notes
              .map((n) => {
                if (typeof n === "object" && n !== null) {
                  const text = n.note || n.text || "";
                  const date = n.createdAt
                    ? new Date(n.createdAt).toLocaleDateString("en-GB")
                    : "";
                  const author = n.userName || n.author || "";
                  if (author && date) return `[${date} - ${author}] ${text}`;
                  if (date) return `[${date}] ${text}`;
                  return text;
                }
                return String(n);
              })
              .filter(Boolean)
              .join(" | ") || "—"
          );
        }
        return String(d.notes) || "—";
      },
    },
    assigneeName: { label: "Assignee Name", map: (d) => d.assigneeName || "—" },
    // POC Fields
    pocIdentified: { label: "POC Identified", map: (d) => d.pocIdentified || "—" },
    pocName: { label: "POC Name", map: (d) => d.pocName || "—" },
    pocPhone: { label: "POC Phone", map: (d) => d.pocPhone || "—" },
    pocEmail: { label: "POC Email", map: (d) => d.pocEmail || "—" },
    pocComments: { label: "POC Comments", map: (d) => d.pocComments || "—" },
  };

  // If no fields selected, use a sane default set
  const fieldsToExport = selectedFields && selectedFields.length > 0
    ? selectedFields
    : ["dealName", "contactName", "mobile", "email", "amount", "stage", "priority", "source", "createdAt", "notes", "assigneeName"];

  // Prepare data for Excel
  const excelData = deals.map((deal, index) => {
    const row = { "S No": index + 1 };
    fieldsToExport.forEach(fieldKey => {
      const field = FIELD_MAPPING[fieldKey];
      if (field) {
        row[field.label] = field.map(deal);
      }
    });
    return row;
  });

  // Create workbook and worksheet
  const ws = XLSX.utils.json_to_sheet(excelData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Leads Export");

  // Auto-fit column widths (rough estimate)
  const colWidths = [{ wch: 8 }]; // S No
  fieldsToExport.forEach(() => colWidths.push({ wch: 20 }));
  ws["!cols"] = colWidths;

  // Generate filename with timestamp
  const timestamp = new Date().toISOString().split("T")[0];
  const fileName = `Leads_Export_${timestamp}.xlsx`;

  // Write and download the file
  XLSX.writeFile(wb, fileName);
};
